/**
 * @file     admin.ts
 * @purpose  Admin Supabase client using service_role key. Bypasses RLS, use only in Route Handlers for privileged operations like creating users.
 */
import { createClient } from '@supabase/supabase-js';
import {
  SERVICE_ROLE_FETCH_TIMEOUT_MS,
  withTransportDeadline,
} from '@/core/supabase/transportDeadline';

let adminClient: ReturnType<typeof createClient> | null = null;

export const getSupabaseAdmin = () => {
  if (adminClient) return adminClient;

  // Read env vars at call time, not module init, prevents Next.js from
  // tree-shaking server-only vars when this module is also imported by client code.
  const url = process.env.NEXT_PUBLIC_SUPABASE_URL
    ?? (process.env.SUPABASE_PROJECT_ID ? `https://${process.env.SUPABASE_PROJECT_ID}.supabase.co` : '');
  const secretKey = process.env.SUPABASE_SECRET_KEY ?? '';

  if (!url || !secretKey) {
    console.warn('[supabase/admin] Missing SUPABASE_SECRET_KEY or URL, admin client unavailable');
    return null;
  }

  adminClient = createClient(url, secretKey, {
    auth: {
      autoRefreshToken: false,
      persistSession: false,
    },
    global: {
      // Same transport deadline as getServiceRoleClient: this client runs the
      // middleware rate limiter and the auth routes, which must never hold a
      // connection for longer than the database's own 60s service_role bound.
      fetch: (input, init) =>
        fetch(input, withTransportDeadline(init, SERVICE_ROLE_FETCH_TIMEOUT_MS)),
    },
  });
  return adminClient;
};
